<template>
  <v-form
    ref="campaignForm"
    v-model="isFormValid"
    class="campaign-form"
    @submit.prevent="submitForm"
  >
    <v-row>
      <v-col cols="12">
        <label class="input-label">Nazwa kampanii *</label>
        <v-text-field
          v-model="formData.name"
          v-bind="staticData.props.validatedField"
          placeholder="Wprowadź tekst"
          :rules="[staticData.rules.required, staticData.rules.maxLength(50)]"
        />
      </v-col>
      <v-col cols="6">
        <label class="input-label">Termin rozpoczęcia *</label>
        <v-menu
          ref="startDatePicker"
          v-model="isStartDatePickerShown"
          v-bind="staticData.props.datePickerMenu"
        >
          <template #activator="{ on, attrs }">
            <div v-on="on">
              <v-text-field
                :value="formData.startDate | displayDate"
                :rules="[staticData.rules.required]"
                v-bind="{ ...attrs, ...staticData.props.dateTextField }"
              />
            </div>
          </template>
          <v-date-picker
            v-model="formData.startDate"
            v-bind="staticData.props.datePicker"
            :max="formData.endDate"
          />
        </v-menu>
      </v-col>
      <v-col cols="6">
        <label class="input-label">Termin zakończenia *</label>
        <v-menu
          ref="endDatePicker"
          v-model="isEndDatePickerShown"
          v-bind="staticData.props.datePickerMenu"
        >
          <template #activator="{ on, attrs }">
            <div v-on="on">
              <v-text-field
                :value="formData.endDate | displayDate"
                :rules="[staticData.rules.required]"
                v-bind="{ ...attrs, ...staticData.props.dateTextField }"
              />
            </div>
          </template>
          <v-date-picker
            v-model="formData.endDate"
            v-bind="staticData.props.datePicker"
            :min="formData.startDate"
          />
        </v-menu>
      </v-col>

      <v-col cols="12">
        <label class="input-label">Projekty</label>
        <v-autocomplete
          v-model="formData.projectIds"
          :items="parsedProjectList"
          placeholder="Wybierz projekty"
          :item-text="(item) => textExcerpt(item.name)"
          item-value="id"
          v-bind="staticData.props.chipsMultiselect"
        >
          <template #item="{ item }">
            <div
              class="w-100 d-flex justify-space-between"
              :class="{ 'grey--text': isArchived(item) }"
            >
              {{ textExcerpt(item.name) }}
              <span
                v-if="isArchived(item)"
                class="error--text ml-1"
              >(Zarchiwizowany)</span>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <v-btn
      class="custom-submit-button mt-4"
      color="primary"
      type="submit"
    >
      {{ campaign ? 'Zapisz' : 'Stwórz' }}
    </v-btn>
  </v-form>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import props from '@/helpers/componentsProps';
import rules from '@/helpers/validators';
import utils from '@/helpers/utils';
import cloneDeep from 'lodash.clonedeep';

export default {
  props: {
    campaign: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      isFormValid: null,
      isStartDatePickerShown: false,
      isEndDatePickerShown: false,
      formData: {
        name: '',
        projectIds: [],
        startDate: '',
        endDate: '',
        description: '',
      },
      staticData: {
        props,
        rules,
      },
    };
  },

  created() {
    this.resetProjects();
    this.setItemsPerPage(9999);
    const filters = this.campaign
      ? {
          statuses: [
            ...this.filtersOptions.projectStatuses
              .filter((status) => status.step !== 1)
              .map((status) => status.status),
            'Zaakceptowany i zarchiwizowany', // archived status is not in statuses array in filters, therefore must be added manually
          ],
        }
      : undefined;
    this.setFilters(filters);
    this.fetchProjects();
    this.setItemsPerPage(12);

    if (this.campaign) {
      this.formData = cloneDeep(this.campaign);
      this.formData.projectIds = this.formData.projects.map((project) => project.id);
      this.$delete(this.formData, 'projects');
    }
  },

  computed: {
    ...mapState('projects', ['projectsList']),
    ...mapState('filters', ['filtersOptions']),

    parsedProjectList() {
      // filter deleted or archived projects (not included in campaign)
      return this.projectsList
        .filter(
          (project) =>
            !project.softDeleted &&
            (!this.isArchived(project) || this.formData.projectIds.includes(project.id))
        )
        .map((project) => ({
          ...project,
          disabled: this.isArchived(project),
        }))
        .sort((projA, projB) => this.isArchived(projA) - this.isArchived(projB));
    },
  },

  methods: {
    ...mapActions('campaigns', ['createCampaign', 'updateCampaign']),
    ...mapActions('projects', ['fetchProjects', 'resetProjects']),
    ...mapActions('snackbar', ['showSnackbar']),
    ...mapActions('dialog', ['clearDialog']),
    ...mapActions('pagination', ['setItemsPerPage']),
    ...mapActions('filters', ['setFilters']),

    textExcerpt: (name) => utils.textExcerpt(name, 50),

    isArchived: (project) => project.status === 'Zaakceptowany i zarchiwizowany',

    showErrors(errors) {
      this.showSnackbar({
        message: errors,
        type: 'error',
      });
    },

    submitForm() {
      this.$refs.campaignForm.validate();

      if (this.isFormValid) {
        const submitMethod = this.campaign ? this.updateCampaign : this.createCampaign;
        submitMethod(this.formData).then(() => this.clearDialog());
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.campaign-form {
  &::v-deep .col {
    padding-top: 0px;
    padding-bottom: 0px;
  }
}
</style>
